import moment from 'moment';
import { FieldKey } from '../../context/field-keys.js';
import { MONTHS_PER_YEAR } from '../../shared/conversion.js';
/**
 * Lease term ⇄ dates rules — DUAL-VARIANT replication of the legacy FE form listeners
 * (T2.1, engineering decision 2026-08-19: Option B + three ratified fixes).
 *
 * The three rules below (formulaVersion 2.0.0) replicate, byte-for-byte where observable, the two
 * patched FE listeners on branch `shiva/calc-core-fe`:
 *
 *  - NON-UK variant (`deps.isUk` falsy):
 *    `marketiq-frontend/src/app/records/controls/common/listeners/lease/auto-calculation-rent-term.ts`
 *  - UK variant (`deps.isUk === true`):
 *    `.../lease/auto-calculation-rent-term-uk.ts`
 *
 * The three RATIFIED FIXES (2026-08-19) baked into both variants (spike doc
 * `calc-core-spike-T0.1-lease-term-rounding.md` §4 decision note):
 *  - F1: blank Term-UOM basis default restored to written intent — non-UK: locale ∈
 *        {US, China(zh), China(en), HongKong(en)} → 'Months', everything else → 'Years';
 *        UK: always 'Years'. (Fixes the `isChinaOrHkOrUs(callOptions)` object-vs-locale compare
 *        that made the default always 'Years' in production — spike §1 I1, §2 V8.)
 *  - F2: a term of `''` / `'0'` / numeric 0 NEVER drives a date computation, in either variant,
 *        on either trigger path (term-change and date-change). (spike §2 V11)
 *  - F3: an unknown / unmapped Term UOM (anything not in {@link DURATION_CATALOG}) → the rule
 *        writes NOTHING (kills the `moment.add(n, undefined)` milliseconds fallback — spike §2 V13).
 *
 * Everything else replicates the listeners AS-IS, including these pinned quirks:
 *  - term-from-dates uses `Math.round(diff(…, unit, true))` — NOT the kernel thresholds
 *    (spike §2 V1–V5 stay listener-column behavior);
 *  - end-before-start writes a NEGATIVE term (spike §2 V6/V7 — explicitly NOT fixed);
 *  - a user-picked Term UOM always wins over the locale basis (spike §2 V9/V12 — v1's
 *    `uomCorrect` guard and basis-overwrite are gone from the rules);
 *  - the term value is written as a STRING (`Math.round(…) + ''`), matching the listeners
 *    (spike §2 type note).
 *
 * Context I/O contract (kept from v1 so consumers survive):
 *  - {@link FieldKey.LeaseTermMonths} carries the term VALUE. Despite the key name it is the term
 *    in the unit named by {@link FieldKey.LeaseTermUom} (or the variant default when the UOM is
 *    blank) — exactly like the listeners' `lease_term` control. term-from-dates writes it as a
 *    string; the date rules accept string or number.
 *  - Dates are `'YYYY-MM-DDTHH:mm:ssZ'`-style strings in, `'YYYY-MM-DDTHH:mm:ss[Z]'` (UTC) out.
 *  - Rules only write when the value actually changed (listener `old !== new` compares).
 *
 * HOST-OWNED (deliberately NOT modelled here): the RxJS plumbing (debounce, combineLatest/skip,
 * the UK circular-update guard), `isMasked` filtering, `emitEvent`/dirty/touched flags, and the
 * `updateGeneral` TRIGGER ROUTING. Hosts must route exactly like the listeners
 * (non-UK `updateGeneral` L179–191; UK `_updateDateFromTerm` L205–225 + `updateGeneral` L233–246):
 *  - date changed → term blank/'0' → run `lease.term.from-dates`;
 *                   exactly one date present (and term valid) → run the missing-date rule;
 *                   both dates present → run `lease.term.from-dates`;
 *  - term/UOM changed → start present → run `lease.end-date.from-start-term`;
 *                       else end present → run `lease.start-date.from-end-term`.
 *  The in-rule guards below make a mis-routed invocation a safe no-op.
 */
export const UTC_EDP_INPUT_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
export const UTC_EDP_OUTPUT_FORMAT = 'YYYY-MM-DDTHH:mm:ss[Z]';
/** Months between two ISO dates, with +1 month when the residual is 15+ days. */
export function calculateLeaseTermMonthly(startDate, endDate) {
  const stdEndDate = moment(endDate, UTC_EDP_INPUT_FORMAT).utc();
  const stdStartDate = moment(startDate, UTC_EDP_INPUT_FORMAT).utc();
  const term = stdEndDate.diff(stdStartDate, 'months', true);
  let monthsDifference = Math.trunc(term);
  const daysDifference = stdEndDate.diff(stdStartDate.add(monthsDifference, 'months'), 'days', true);
  monthsDifference = daysDifference > 14 ? monthsDifference + 1 : monthsDifference;
  return monthsDifference;
}
/** Years between two ISO dates, with +1 year when the residual is 6+ months. 0 when under a year. */
export function calculateLeaseTermYearly(startDate, endDate) {
  const stdEndDate = moment(endDate, UTC_EDP_INPUT_FORMAT).utc();
  const stdStartDate = moment(startDate, UTC_EDP_INPUT_FORMAT).utc();
  const term = stdEndDate.diff(stdStartDate, 'years', true);
  let yearsDifference = Math.trunc(term);
  if (yearsDifference > 0) {
    const monthsDifference = stdEndDate.diff(stdStartDate.add(yearsDifference, 'years'), 'months', true);
    yearsDifference = monthsDifference >= 6 ? yearsDifference + 1 : yearsDifference;
  }
  return yearsDifference;
}
/** End date = start + term (months|years) − 1 day, in UTC output format. */
export function calculateEndDate(startDate, term, basis) {
  return moment(startDate, UTC_EDP_INPUT_FORMAT).utc().add(term, basis).subtract(1, 'days').format(UTC_EDP_OUTPUT_FORMAT);
}
/** Start date = end − term (months|years) + 1 day, in UTC output format. */
export function calculateStartDate(endDate, term, basis) {
  return moment(endDate, UTC_EDP_INPUT_FORMAT).utc().subtract(term, basis).add(1, 'days').format(UTC_EDP_OUTPUT_FORMAT);
}
/**
 * True when the term UOM matches the locale basis (US/months → 'Months', UK·NL/years → 'Years').
 *
 * NOTE (v2.0.0): no longer used by the rules below — the listeners honor the user's UOM
 * (spike §2 V9/V12). Kept exported untouched for the shared-models business rule (I4) and
 * bulkedit parity; its retirement is a separate decision.
 */
export function uomCorrect(basis, uom) {
  return basis === 'months' && uom === 'Months' || basis === 'years' && uom === 'Years';
}
/**
 * Split a whole-month lease term into years + remaining months (e.g. 30 → { years: 2, months: 6 }).
 * Consolidates the `Math.floor(months/12)` + `months % 12` math duplicated across the lease-term
 * display components.
 */
export function splitTermMonths(totalMonths) {
  return {
    years: Math.floor(totalMonths / 12),
    months: totalMonths % 12
  };
}
/**
 * Combine a years value and a months value into a single total-months figure
 * (e.g. (2, 6) → 30). The exact inverse of {@link splitTermMonths}; consolidates the
 * `years * 12 + months` math duplicated across the lease-term input components
 * (lease-term + lease-term-std). Callers keep their own `Number(...)` coercion of form
 * values, so empty sub-fields (`''`) still coerce to 0 before being passed in.
 */
export function yearsMonthsToTotalMonths(years, months) {
  return years * MONTHS_PER_YEAR + months;
}
/**
 * Term-UOM label → moment unit. Verbatim copy of `DURATION_CATALOG` in
 * `auto-calculation-rent-rates.model.ts` L73–78 (both listeners index into it via
 * `getUnitOfTime`, non-UK L197–200 / UK L254–257).
 *
 * REPLICATED AS-IS: the literal label `'Unknown'` is a KNOWN catalog entry mapping to months —
 * only labels absent from this catalog trigger the F3 no-write guard.
 */
export const DURATION_CATALOG = {
  Days: 'days',
  Years: 'years',
  Months: 'months',
  Unknown: 'months'
};
/**
 * RATIFIED FIX (2026-08-19) F1 — the locales whose blank-UOM term default is 'Months'.
 *
 * `deps.locale` contract (serialisable, host-agnostic — accepted in BOTH forms):
 *  - the literal numeric `LocaleId` values from
 *    `marketiq-frontend/projects/miq/shared-models/src/lib/models/language.model.ts` L2–39:
 *    `LocaleId.US = 1`, `LocaleId.ChinaChinese = 3`, `LocaleId.ChinaEnglish = 4`,
 *    `LocaleId.HongKongEnglish = 40`;
 *  - the equivalent BCP-47-ish strings `'en-US'`, `'zh-CN'`, `'en-CN'`, `'en-HK'`.
 * Any other value (including other LocaleIds, other strings, `undefined`) → 'Years' default,
 * exactly like the listener's `countryList.includes(callOptions?.locale)` else-branch.
 */
export const LEASE_TERM_MONTHS_DEFAULT_LOCALE_IDS = [1, 3, 4, 40];
/** String form of the same four locales — see {@link LEASE_TERM_MONTHS_DEFAULT_LOCALE_IDS}. */
export const LEASE_TERM_MONTHS_DEFAULT_LOCALE_CODES = ['en-US', 'zh-CN', 'en-CN', 'en-HK'];
/**
 * RATIFIED FIX (2026-08-19) F1 — `isChinaOrHkOrUs`, fixed. Replicates the WRITTEN INTENT of
 * `auto-calculation-rent-term.ts` L109–119: the deployed code compared the whole `callOptions`
 * OBJECT against numeric LocaleIds, so it was always false and every blank-UOM default resolved
 * to 'Years' (spike §1 I1, §2 V8). The patched listener — and this port — compare the locale.
 */
export function isChinaOrHkOrUsLocale(locale) {
  if (locale == null) {
    return false;
  }
  return typeof locale === 'number' ? LEASE_TERM_MONTHS_DEFAULT_LOCALE_IDS.includes(locale) : LEASE_TERM_MONTHS_DEFAULT_LOCALE_CODES.includes(locale);
}
/**
 * The variant's `calculationType` — the Term-UOM default used when the UOM field is blank
 * (listener `setTermValue` non-UK L192–196 / UK L248–252) and as the blank-UOM unit for the math
 * (`getUnitOfTime`, non-UK L197–200 / UK L254–257).
 *
 * Precedence (documented contract):
 *  1. `deps.isUk === true` → 'Years', always — UK listener L126 (`calculationType = TermUnits.Years`).
 *  2. `deps.locale` present → F1 mapping — non-UK listener L92 + L109–119
 *     (`isChinaOrHkOrUs ? Months : Years`). See {@link isChinaOrHkOrUsLocale} for accepted forms.
 *  3. `deps.locale` absent → `deps.leaseTermBasis` is honoured as an ADVISORY fallback
 *     ('months' → 'Months', 'years' → 'Years') so pre-2.0.0 consumers that only inject the basis
 *     keep a sensible default. The basis decision itself now lives INSIDE the rules.
 *  4. Nothing supplied → 'Years' (the listener's else-branch for every unlisted locale).
 */
export function defaultLeaseTermUom(deps) {
  if (deps?.isUk === true) {
    return 'Years';
  }
  const locale = deps?.locale;
  if (locale != null && locale !== '') {
    return isChinaOrHkOrUsLocale(locale) ? 'Months' : 'Years';
  }
  if (deps?.leaseTermBasis === 'months') {
    return 'Months';
  }
  if (deps?.leaseTermBasis === 'years') {
    return 'Years';
  }
  return 'Years';
}
/**
 * `simpleValueValidation` — verbatim semantics of
 * `marketiq-frontend/projects/miq/utils/src/lib/core-utils/validators.ts` L6:
 * present ⇔ not null/undefined and not blank after trim.
 */
const isPresent = val => val != null && String(val).trim() !== '';
/**
 * RATIFIED FIX (2026-08-19) F2 — `hasTermValue`: `''`/`'0'`/numeric 0 (and any falsy term) never
 * drives a date computation. The UK listener always had this on its term-change path (L208);
 * the fix extends it to the non-UK term-change path (`_updateDateFromTerm` L169) and to BOTH
 * variants' date-change routing (`updateGeneral` L181/L236). Baked into the date rules here so
 * every trigger path is covered.
 */
const hasTermValue = termV => !!termV && termV !== '0';
/**
 * `getUnitOfTime` (non-UK L197–200 / UK L254–257): blank UOM → the variant default; then a
 * catalog lookup. Returns undefined for unmapped labels — the F3 no-write guard keys off that.
 * (JS object indexing stringifies keys, so `String(...)` is semantically identical to the
 * listeners' raw index.)
 */
function resolveTermUnit(uomV, defaultUom) {
  const key = uomV ? String(uomV) : defaultUom;
  return DURATION_CATALOG[key];
}
/**
 * `setTermValue` (non-UK L192–196 / UK L248–252): write the variant default into the Term UOM
 * ONLY when it is blank (falsy). A blank → default write is always a real change, so the
 * old-vs-new compare is implicit.
 */
function setTermUomDefaultWhenBlank(ctx, uomV, defaultUom) {
  if (!uomV) {
    ctx.set(FieldKey.LeaseTermUom, defaultUom);
  }
}
/**
 * `updateDate` + `setNewDate` — the shared date-from-term body of both variants
 * (non-UK `updateDate` L16–42 + `setNewDate` L201–221; UK `updateDate` L22–50 + `setNewDate`
 * L259–285). `direction` picks the argument order the listeners hard-code:
 *  - 'end'   (isStartDate=false): end = start.add(term, unit).subtract(1, 'days')
 *  - 'start' (isStartDate=true):  start = end.add(1, 'days').subtract(term, unit)
 * REPLICATED AS-IS: the +1day/−term OPERATION ORDER is observable at month boundaries
 * (e.g. end 2021-02-28, 1 Month → start 2021-02-01, not 2021-01-29) and is kept exactly.
 */
function runDateFromTerm(ctx, deps, direction) {
  const isUk = deps?.isUk === true;
  const baseKey = direction === 'end' ? FieldKey.LeaseStartDate : FieldKey.LeaseEndDate;
  const outKey = direction === 'end' ? FieldKey.LeaseEndDate : FieldKey.LeaseStartDate;
  const baseV = ctx.get(baseKey);
  const oldValue = ctx.get(outKey);
  const termV = ctx.get(FieldKey.LeaseTermMonths);
  const uomV = ctx.get(FieldKey.LeaseTermUom);
  // RATIFIED FIX (2026-08-19) F2: '' / '0' / 0 term → no date computation, either variant,
  // either trigger path (non-UK _updateDateFromTerm L169 / updateGeneral L181; UK L208 / L236).
  if (!hasTermValue(termV)) {
    return;
  }
  // `isDateValidation` (non-UK L164–166) / `hasStartDate`/`hasEndDate` (UK L206–207, L218–224):
  // the base date must be present (simpleValueValidation) before a date is computed.
  if (!isPresent(baseV)) {
    return;
  }
  const defaultUom = defaultLeaseTermUom(deps); // RATIFIED FIX (2026-08-19) F1 via defaultLeaseTermUom
  // RATIFIED FIX (2026-08-19) F3: unknown/unmapped Term UOM → no write at all — the listener
  // returns BEFORE setNewDate AND before setTermValue (non-UK updateDate L28–33 / UK L35–40).
  const unit = resolveTermUnit(uomV, defaultUom);
  if (!unit) {
    return;
  }
  // setNewDate (non-UK L201–221 / UK L259–285).
  const baseMoment = moment(String(baseV), UTC_EDP_INPUT_FORMAT).utc();
  if (isUk && !baseMoment.isValid()) {
    // UK-ONLY guard (UK setNewDate L269–272): invalid base date → no date write. updateDate still
    // falls through to setTermValue afterwards (UK L49), so the blank-UOM default IS written.
    setTermUomDefaultWhenBlank(ctx, uomV, defaultUom);
    return;
  }
  // REPLICATED AS-IS (non-UK): NO validity guard — an unparseable (but present) base date formats
  // to the literal string 'Invalid date' and, being !== the old value, gets WRITTEN (non-UK
  // setNewDate L210–219). Not covered by the ratified fixes; pinned as a quirk.
  const newDate = direction === 'end' ? baseMoment.add(termV, unit).subtract(1, 'days').format(UTC_EDP_OUTPUT_FORMAT) : baseMoment.add(1, 'days').subtract(termV, unit).format(UTC_EDP_OUTPUT_FORMAT);
  // Write only on change (`oldValue !== newDate`, non-UK L215 / UK L280).
  if (oldValue !== newDate) {
    ctx.set(outKey, newDate);
  }
  // setTermValue runs UNCONDITIONALLY after setNewDate (non-UK updateDate L41 / UK L49) — the
  // blank-UOM default is written even when the date itself did not change.
  setTermUomDefaultWhenBlank(ctx, uomV, defaultUom);
}
/**
 * Compute End Date from Start + Term — `updateDate(ctx, false)` of both listeners, as routed by
 * the term-change path (start present) and the date-change path (end missing).
 *
 * v2.0.0 (2026-08-19, Option B dual-variant): see the module JSDoc for the variant split, the
 * ratified fixes (F1/F2/F3) and the pinned quirks. Guard deltas vs v1: the `uomCorrect` basis
 * gate is GONE (a user UOM always computes — spike §2 V12 listener column), the blank-UOM
 * default comes from F1, and the rule also writes the Term-UOM default when blank (`setTermValue`
 * semantics), hence the extra output.
 *
 * Note on inputs: the old End value is also read, but only for the write-on-change compare —
 * it is deliberately not declared as an input (it is this rule's output).
 */
export const leaseEndDateFromStartTermRule = {
  id: 'lease.end-date.from-start-term',
  inputs: [FieldKey.LeaseStartDate, FieldKey.LeaseTermMonths, FieldKey.LeaseTermUom],
  outputs: [FieldKey.LeaseEndDate, FieldKey.LeaseTermUom],
  formulaVersion: '2.0.0',
  run: (ctx, deps) => runDateFromTerm(ctx, deps, 'end')
};
/**
 * Compute Start Date from End + Term — `updateDate(ctx, true)` of both listeners, as routed by
 * the term-change path (start missing, end present) and the date-change path (start missing).
 *
 * v2.0.0 (2026-08-19, Option B dual-variant): same variant split, fixes and quirks as
 * {@link leaseEndDateFromStartTermRule}; the math is `end + 1 day − term` in that exact
 * operation order.
 */
export const leaseStartDateFromEndTermRule = {
  id: 'lease.start-date.from-end-term',
  inputs: [FieldKey.LeaseEndDate, FieldKey.LeaseTermMonths, FieldKey.LeaseTermUom],
  outputs: [FieldKey.LeaseStartDate, FieldKey.LeaseTermUom],
  formulaVersion: '2.0.0',
  run: (ctx, deps) => runDateFromTerm(ctx, deps, 'start')
};
/**
 * Compute Term (+ its UOM default when blank) from Start & End — `updateTerm` of both listeners
 * (non-UK L43–74 / UK L52–86), as routed by the date-change path (term blank/'0', or both dates
 * present).
 *
 * v2.0.0 (2026-08-19, Option B dual-variant) — deltas vs v1:
 *  1. Term = `Math.round(end.diff(start, unit, true)) + ''` — a STRING, in the unit of the
 *     current UOM (or the variant blank-UOM default), NOT the kernel thresholds.
 *     REPLICATED AS-IS: spike §2 V1–V5 pin the listener column (half-month rounds DOWN, etc.).
 *  2. REPLICATED AS-IS: no end-after-start guard — end < start writes a NEGATIVE term
 *     (spike §2 V6/V7; explicitly not fixed).
 *  3. The user's UOM is honoured, never overwritten (spike §2 V9); the UOM is written only when
 *     blank (`setTermValue`), and only inside the term-changed block (non-UK L67–72 / UK L79–84).
 *  4. RATIFIED FIX (2026-08-19) F1: the blank-UOM unit/default is locale-driven — see
 *     {@link defaultLeaseTermUom}. F3: an unmapped UOM label → no write (guard existed in
 *     `updateTerm` L61–64 and is kept).
 *
 * Note on inputs: the old Term value is also read, but only for the write-on-change compare —
 * it is deliberately not declared as an input (it is this rule's output).
 */
export const leaseTermFromDatesRule = {
  id: 'lease.term.from-dates',
  inputs: [FieldKey.LeaseStartDate, FieldKey.LeaseEndDate, FieldKey.LeaseTermUom],
  outputs: [FieldKey.LeaseTermMonths, FieldKey.LeaseTermUom],
  formulaVersion: '2.0.0',
  run: (ctx, deps) => {
    const startV = ctx.get(FieldKey.LeaseStartDate);
    const endV = ctx.get(FieldKey.LeaseEndDate);
    const termV = ctx.get(FieldKey.LeaseTermMonths);
    const uomV = ctx.get(FieldKey.LeaseTermUom);
    // multipleSimpleValueValidation(start, end) — both dates present (non-UK L55 / UK L65).
    if (!isPresent(startV) || !isPresent(endV)) {
      return;
    }
    // REPLICATED AS-IS: local-mode moments (no .utc()) on the diff path, exactly like updateTerm
    // (non-UK L56–57 / UK L66–67). diff() is instant-based, so results match the UTC kernels.
    const startMoment = moment(String(startV), UTC_EDP_INPUT_FORMAT);
    const endMoment = moment(String(endV), UTC_EDP_INPUT_FORMAT);
    if (!startMoment.isValid() || !endMoment.isValid()) {
      return;
    }
    const defaultUom = defaultLeaseTermUom(deps); // RATIFIED FIX (2026-08-19) F1
    // RATIFIED FIX (2026-08-19) F3: unmapped UOM label → no write (updateTerm L61–64).
    const unit = resolveTermUnit(uomV, defaultUom);
    if (!unit) {
      return;
    }
    // REPLICATED AS-IS: Math.round of the fractional diff (non-UK L65–66 / UK L76–77) — including
    // NEGATIVE terms when end < start (spike §2 V6/V7, no order guard) — written as a STRING.
    const newTermDiff = endMoment.diff(startMoment, unit, true);
    const newTerm = Math.round(newTermDiff) + '';
    // Write only on change — strict compare against the raw old value (non-UK L67 / UK L79), so a
    // NUMERIC 12 is replaced by the string '12' (listener quirk, pinned). setTermValue runs only
    // inside this changed-block: an unchanged term leaves a blank UOM blank.
    if (termV !== newTerm) {
      ctx.set(FieldKey.LeaseTermMonths, newTerm);
      setTermUomDefaultWhenBlank(ctx, uomV, defaultUom);
    }
  }
};
